import React from 'react'
import NextLink from 'next/link'
import NextImage from 'next/image'
import { Heading, Paragraph } from '@alto-avios/alto-ui'
import {
  documentToReactComponents,
  Options,
  RenderNode,
} from '@contentful/rich-text-react-renderer'
import { INLINES, MARKS, BLOCKS, Node } from '@contentful/rich-text-types'
import { IRichTextProps } from './RichText.types'

import styles from './RichText.module.scss'

const defaultOptions: Options = {
  renderMark: {
    [MARKS.BOLD]: (text: React.ReactNode) => <strong>{text}</strong>,
  },
  renderNode: {},
}

const sanitizeContentfulSrcUrl = (url?: string) => {
  if (!url) {
    return ''
  }
  if (url.startsWith('//')) {
    return `https:${url}`
  }
  return url
}

const getParagraphNodes = (sizes?: IRichTextProps['sizes']) => {
  const options: Options['renderNode'] = {
    [BLOCKS.PARAGRAPH]: (node, children) => <Paragraph size={sizes?.p}>{children}</Paragraph>,
  }
  return options
}

const getHeadingNodes = (sizes?: IRichTextProps['sizes']) => {
  const options: Options['renderNode'] = {
    [BLOCKS.HEADING_1]: (node, children) => (
      <Heading as="h1" size={sizes?.h1}>
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <Heading as="h2" size={sizes?.h2}>
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <Heading as="h3" size={sizes?.h3}>
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <Heading as="h4" size={sizes?.h4}>
        {children}
      </Heading>
    ),
  }
  return options
}

const getLinkNode = (openLinksInNewTab?: boolean) => ({
  [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) => {
    const { data } = node
    const { uri } = data
    const additionalProps = openLinksInNewTab
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : null
    return (
      <NextLink className={styles.hyperlink} href={uri} {...additionalProps}>
        {children}
      </NextLink>
    )
  },
})

const imageMimeTypeRegex = /([^\s]+(\/(jpe?g|png|gif|bmp|svg\+xml))$)/
const isImage = (mimeType?: string) => mimeType && imageMimeTypeRegex.test(mimeType)

const getInlineAssetNodes = () => ({
  [BLOCKS.EMBEDDED_ASSET]: (node: Node, children: React.ReactNode) => {
    const {
      data: {
        target: { file, title },
      },
    } = node
    const { url, contentType, details } = file
    const imageSrc = sanitizeContentfulSrcUrl(url)

    if (isImage(contentType)) {
      return <NextImage src={imageSrc} alt={title} {...details.image} />
    }

    return children
  },
})

const getInlineEntryNodes = (
  inlineEntryRenderer?: IRichTextProps['inlineEntryRenderer']
): RenderNode => {
  const options: RenderNode = {
    [INLINES.EMBEDDED_ENTRY]: (node, children) =>
      inlineEntryRenderer?.(node.data.target) || children,
  }
  return options
}

// @contentful/rich-text-react-renderer does not render line breaks when converting from markdown to rich text so we need to handle them manually by replacing '\n' with <br />
const renderTextWithTags = (text: string) => {
  if (text === '\n') {
    return <br />
  }
  return text
}

const RichText: React.FC<IRichTextProps> = ({
  document,
  openLinksInNewTab,
  inlineEntryRenderer,
  inlineTextEntryRenderer,
  sizes,
}) => {
  const options: Options = {
    renderMark: {
      ...defaultOptions.renderMark,
    },
    renderNode: {
      ...defaultOptions.renderNode,
      ...getParagraphNodes(sizes),
      ...getHeadingNodes(sizes),
      ...getLinkNode(openLinksInNewTab),
      ...getInlineAssetNodes(),
      ...getInlineEntryNodes(inlineEntryRenderer),
    },
    renderText: (text) => {
      return inlineTextEntryRenderer ? inlineTextEntryRenderer(text) : renderTextWithTags(text)
    },
  }
  return <div className={styles.richText}>{documentToReactComponents(document, options)}</div>
}

export default RichText
